<script setup lang="ts">
import { ref, computed, provide, onMounted, watch } from 'vue'
import { useFrameStore } from '../stores/frameStore'
import { useUiStore } from '../stores/uiStore'
import { useUserStore } from '../stores/userStore'
import FrameComponent from '../components/Frame.vue'
import Toolbar from '../components/Toolbar.vue'
import InfoLabel from '../components/InfoLabel.vue'
import SnapshotPanel from '../components/SnapshotPanel.vue'
import DesktopMinimap from '../components/DesktopMinimap.vue'
import AuthDialog from '../components/AuthDialog.vue'
import ForgotPasswordDialog from '../components/ForgotPasswordDialog.vue'
import { apiClient } from '../services/apiClient'

const frameStore = useFrameStore()
const uiStore = useUiStore()
const userStore = useUserStore()
const viewportRef = ref<HTMLElement>()
const isInitialized = ref(false)
const showSnapshotPanel = ref(false)
const showAuthDialog = ref(false)
const showForgotPasswordDialog = ref(false)

const isLoggedIn = computed(() => userStore.isLoggedIn)
const currentUser = computed(() => userStore.currentUser)
const userStats = computed(() => userStore.userStats)

const handleAuthLoginSuccess = async () => {
  // 顯示登入成功訊息
  alert('登入成功！')
  showAuthDialog.value = false

  // 嘗試加載用戶的第一筆快照
  try {
    const snapshots = await apiClient.getSnapshots()
    if (snapshots.length > 0) {
      // 自動恢復第一筆快照
      const firstSnapshot = snapshots[0]
      const restoredFrames = await apiClient.restoreSnapshot(firstSnapshot.id)
      frameStore.frames.splice(0, frameStore.frames.length, ...restoredFrames)
    }
  } catch (error) {
    console.error('Failed to load first snapshot:', error)
    // 如果沒有快照，保留預設頁面
  }
}

const handleLogoutConfirm = async () => {
  await userStore.logout()
  // 重置虛擬桌面為預設狀態
  frameStore.frames.splice(0, frameStore.frames.length)
  // 重新初始化預設框
  await frameStore.initializeFrames()
}

provide('viewportRef', viewportRef)

const toolbarHeight = computed(() => uiStore.toolbarHeight)
const DESKTOP_WIDTH = 4000
const DESKTOP_HEIGHT = 3000
const MIN_FRAME_SPACING = 5
const MIN_WIDTH = 150
const MIN_HEIGHT = 100

onMounted(async () => {
  // 框架初始化由 App.vue 負責，此處只確保有框架存在
  // 如果 frames 為空，說明 App.vue 還沒完成初始化
  // 在這種情況下，延遲等待直到框架被初始化
  if (frameStore.frames.length === 0) {
    // 給 App.vue 充足的時間完成初始化
    await new Promise(resolve => setTimeout(resolve, 100))

    // 如果仍然沒有框架，才手動初始化
    if (frameStore.frames.length === 0) {
      await frameStore.initializeFrames()
    }
  }
  isInitialized.value = true
})


const getInitialFrameDimensions = () => {
  const width = window.innerWidth / 3
  const height = (window.innerHeight - toolbarHeight.value) / 2
  return { width, height }
}

// 儲存版面快照
const saveSnapshot = async () => {
  try {
    await apiClient.saveSnapshot(frameStore.frames)
    alert('版面配置已儲存')
  } catch (error) {
    console.error('Failed to save snapshot:', error)
    alert('儲存版面配置失敗')
  }
}

// 恢復快照
const handleRestoreSnapshot = async (snapshotId: number) => {
  try {
    const restoredFrames = await apiClient.restoreSnapshot(snapshotId)
    frameStore.frames.splice(0, frameStore.frames.length, ...restoredFrames)
    showSnapshotPanel.value = false
    alert('版面配置已恢復')
  } catch (error) {
    console.error('Failed to restore snapshot:', error)
    alert('恢復版面配置失敗')
  }
}

// 清除所有快照
const clearAllSnapshots = async () => {
  if (!confirm('確定要刪除所有版面配置歷史嗎？此操作無法撤銷。')) {
    return
  }
  try {
    await apiClient.deleteAllSnapshots()
    alert('所有版面配置歷史已刪除')
  } catch (error) {
    console.error('Failed to delete snapshots:', error)
    alert('刪除版面配置歷史失敗')
  }
}
</script>

<template>
  <div class="flex flex-col h-screen w-screen bg-gray-100">
    <Toolbar
      :is-logged-in="isLoggedIn"
      :user="currentUser"
      :snapshot-count="userStats.snapshotCount"
      :bookmark-count="userStats.bookmarkCount"
      :note-count="userStats.noteCount"
      @toggle-snapshot-panel="showSnapshotPanel = !showSnapshotPanel"
      @save-snapshot="saveSnapshot"
      @show-auth-dialog="showAuthDialog = true"
      @logout-confirm="handleLogoutConfirm"
    />
    <div
      ref="viewportRef"
      class="flex-1 overflow-auto bg-gray-50 relative"
      style="top: 0; left: 0"
    >
      <div
        class="relative"
        :style="{
          width: DESKTOP_WIDTH + 'px',
          height: DESKTOP_HEIGHT + 'px',
          backgroundColor: '#f9fafb',
        }"
      >
        <FrameComponent
          v-for="frame in frameStore.frames"
          :key="frame.id"
          :frame="frame"
          :toolbar-height="toolbarHeight"
        />
      </div>
    </div>
    <InfoLabel />
    <SnapshotPanel
      :is-open="showSnapshotPanel"
      @restore="handleRestoreSnapshot"
      @close="showSnapshotPanel = false"
      @clear-all-snapshots="clearAllSnapshots"
    />
    <AuthDialog
      :is-open="showAuthDialog"
      @close="showAuthDialog = false"
      @login-success="handleAuthLoginSuccess"
      @forgot-password="
        () => {
          showAuthDialog = false
          showForgotPasswordDialog = true
        }
      "
    />
    <ForgotPasswordDialog
      :is-open="showForgotPasswordDialog"
      @close="showForgotPasswordDialog = false"
    />
    <DesktopMinimap />
  </div>
</template>

<style scoped>
:deep(.frame-title) {
  cursor: grab;
}

:deep(.frame-title:active) {
  cursor: grabbing;
}

:deep(.frame-resize) {
  cursor: nwse-resize;
}
</style>
